import { Avatar, Grid, Group, Text } from '@mantine/core';
import { ActiveLeadState, LeadStatus } from '@bariendo/lib/active-lead';
import { LeadStatusMenu } from '@/components/LeadStatus/LeadStatusMenu';
import { ORBIT_DISABLED, ORBIT_GRAY, ORBIT_NEEDS_ACTION, ORBIT_SETTLED } from '@/theme/colors';
import { getInitials } from '../shared/IdentityRow';
import { LockedValue } from './LockedValue';

/** The two statuses only the system sets, after the win. */
export type FinalLeadStatus = 'won' | 'cancelled';

const FINAL_STATUS: Record<FinalLeadStatus, { label: string; dotColor: string; reason: string }> = {
  won: { label: 'Won', dotColor: ORBIT_SETTLED, reason: 'Set when the patient was won' },
  cancelled: { label: 'Cancelled', dotColor: ORBIT_DISABLED, reason: 'Set by the cancellation request' },
};

function LabelCell() {
  return (
    <Grid.Col span={3}>
      <Text style={{ fontSize: 13, lineHeight: '16px', color: ORBIT_GRAY }}>Lead status</Text>
    </Grid.Col>
  );
}

/**
 * Won and Cancelled: the row stays after the win and locks. There is no Refunded lead status;
 * whether the money has gone back is the Payment row's to say.
 */
export function FinalLeadStatusRow({ status }: { status: FinalLeadStatus }) {
  const { label, dotColor, reason } = FINAL_STATUS[status];

  return (
    <Grid align="flex-start" style={{ width: '100%' }}>
      <LabelCell />
      <Grid.Col span={9}>
        <LockedValue reason={reason}>
          <Text
            component="span"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, lineHeight: '20px' }}
          >
            <span className="lead-status-dot" style={{ background: dotColor }} />
            {label}
          </Text>
        </LockedValue>
      </Grid.Col>
    </Grid>
  );
}

interface LeadStatusRowProps {
  state: ActiveLeadState;
  /** Display name of the practitioner who parked the lead, for the avatar. */
  overrideAuthorName?: string;
  onChange: (status: LeadStatus) => void;
}

const LOCKED_TOOLTIP = 'Consult booked, status is locked';

/**
 * The panel's Lead status row: the status menu, then the reason line. The
 * reason comes verbatim from `deriveActiveLead`, so the panel and lists share
 * one vocabulary; a parked lead's line leads with who parked it.
 */
export function LeadStatusRow({ state, overrideAuthorName, onChange }: LeadStatusRowProps) {
  const [head, ...rest] = state.reason.split(' · ');

  return (
    <>
      <Grid align="flex-start" style={{ width: '100%' }}>
        <LabelCell />
        <Grid.Col span={9}>
          <LeadStatusMenu
            status={state.status}
            onChange={onChange}
            disabled={state.locked}
            tooltip={state.locked ? LOCKED_TOOLTIP : undefined}
          />
        </Grid.Col>
      </Grid>
      <Group gap={6} wrap="nowrap" align="center">
        {state.overrideAuthor && (
          <Avatar size={24} radius="xl" color="green" variant="light">
            <Text style={{ fontSize: 10, fontWeight: 600 }}>{getInitials(overrideAuthorName)}</Text>
          </Avatar>
        )}
        <Text
          style={{
            fontSize: 13,
            lineHeight: '16px',
            color: state.reasonWarning ? ORBIT_NEEDS_ACTION : ORBIT_GRAY,
            fontWeight: state.reasonWarning ? 600 : 400,
          }}
        >
          {state.overrideAuthor ? (
            <>
              <span style={{ color: 'var(--mantine-color-black)' }}>{head}</span>
              {rest.length > 0 && ` · ${rest.join(' · ')}`}
            </>
          ) : (
            state.reason
          )}
        </Text>
      </Group>
    </>
  );
}
